import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Breadcrumbs from '../../components/BreadCrumbs';
import StyledHeadingText from '../../components/Fonts/StyledHeadingText';
import Table from '../../components/tables/Table';
import Button from '../../components/buttons/Button';
import { CustomCard } from '../../components/cards/CustomCard';
import SearchBar from '../../components/searchBar/SearchBar';
import DropDown from '../../components/inputFields/DropDown';
import { Link } from 'react-router-dom';
import { AlertCircleIcon, BoxesIcon } from 'lucide-react';
import { getKitDropdown, getRawMaterialInventoryView } from '../../services/inventory';
import { toast } from 'react-toastify';
import CustomLoading from '../../components/customLoading/CustomLoading';
import Pagination from '../../components/tables/pagination/Pagination';
import ShowModal from '../../utils/ShowModal';
import RawInventoryDetailedView from './RawInventoryDetailedView';
import Card from '../../components/cards/Card';

const RawItemsInventory = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    // Helper functions for URL parameters
    const getUrlParam = (key, defaultValue = '') => {
        return searchParams.get(key) || defaultValue;
    };

    const setUrlParams = (params) => {
        const newSearchParams = new URLSearchParams(searchParams);
        Object.entries(params).forEach(([key, value]) => {
            if (value && value !== '' && value !== null) {
                newSearchParams.set(key, value);
            } else {
                newSearchParams.delete(key);
            }
        });
        setSearchParams(newSearchParams, { replace: true });
    };

    // Initialize state from URL parameters
    const [searchTerm, setSearchTerm] = useState(() => getUrlParam('search', ''));
    const [filterCategory, setFilterCategory] = useState(() => getUrlParam('category', ''));
    const [filterOrderBy, setFilterOrderBy] = useState(() => getUrlParam('order_by', 'asc'));
    const [filterKit, setFilterKit] = useState(() => getUrlParam('kit', ''));
    const [kitDropdown, setKitDropdown] = useState([]);

    const [rawItems, setRawItems] = useState({});
    const [currentPage, setCurrentPage] = useState(() => parseInt(getUrlParam('page', '1')));
    const pageSize = 10;
    const [loading, setLoading] = useState(false);
    const [showRawMaterialDetailsModal, setShowRawMaterialDetailsModal] = useState(false);
    const [selectedRawMaterial, setSelectedRawMaterial] = useState(null);

    const categories = [
        { value: '', label: 'All' },
        { value: 'Electrical', label: 'Electrical' },
        { value: 'Electronics', label: 'Electronics' },
        { value: 'PCB', label: 'PCB' },
        { value: 'Mechanical', label: 'Mechanical' },
        { value: 'Plastic', label: 'Plastic' },
        { value: 'Metal', label: 'Metal' },
        { value: 'Wire', label: 'Wire' },
        { value: 'Other', label: 'Other' }
    ];

    const [statsCardsData, setStatsCardsData] = useState([]);

    const getStatusBadge = (status) => {
        const baseClasses = "px-2 py-1 rounded-full text-xs font-medium";
        switch (status) {
            case 'critical':
                return `${baseClasses} bg-red-100 text-red-800`;
            case 'warning':
                return `${baseClasses} bg-yellow-100 text-yellow-800`;
            case 'good':
                return `${baseClasses} bg-green-100 text-green-800`;
            default:
                return `${baseClasses} bg-gray-100 text-gray-800`;
        }
    };

    const fetchKitDropdown = async () => {
        try {
            let res = await getKitDropdown();
            if (res.status === 200) {
                const data = res.data;
                setKitDropdown([{ id: '', name: 'All' }, ...data]);
            } else {
                toast.error("Unable to fetch Data");
            }
        } catch (error) {
            console.error("Error:", error);
        }
    };

    useEffect(() => {
        // Build initial query from URL parameters
        const initialQuery = `search=${searchTerm}&category=${filterCategory}&order_by_quantity=${filterOrderBy}&kit=${filterKit}`;
        fetchData({ query: initialQuery });
        fetchKitDropdown();
    }, []);

    // Update URL parameters when filters change
    useEffect(() => {
        const params = {
            search: searchTerm,
            category: filterCategory,
            order_by: filterOrderBy,
            kit: filterKit,
            page: currentPage.toString()
        };
        setUrlParams(params);
    }, [searchTerm, filterCategory, filterOrderBy, filterKit, currentPage]);

    const handleSearch = (query) => {
        setSearchTerm(query);
        fetchData({ query: `search=${query}&category=${filterCategory}&order_by_quantity=${filterOrderBy}&kit=${filterKit}` });
    };

    const handleFilterCategory = (category) => {
        setFilterCategory(category);
        fetchData({ query: `search=${searchTerm}&category=${category}&order_by_quantity=${filterOrderBy}&kit=${filterKit}` });
    };

    const handleOrderBy = (orderBy) => {
        setFilterOrderBy(orderBy);
        fetchData({ query: `search=${searchTerm}&category=${filterCategory}&order_by_quantity=${orderBy}&kit=${filterKit}` });
    };

    const handleFilterKit = (kit) => {
        setFilterKit(kit);
        fetchData({ query: `search=${searchTerm}&category=${filterCategory}&order_by_quantity=${filterOrderBy}&kit=${kit}` });
    };

    const fetchData = async ({ query = "" }) => {
        try {
            setLoading(true);
            const res = await getRawMaterialInventoryView(`?${query}`);
            if (res.status === 200) {
                setRawItems(res.data);
                setStatsCardsData([
                    {
                        title: "Total Raw Items",
                        count: res.data.stats[0].value,
                        icon: BoxesIcon,
                        bgGradient: "from-blue-50 to-blue-100",
                        textColor: "text-blue-900"
                    },
                    {
                        title: "Low Stock Alerts",
                        count: res.data.stats[1].value,
                        icon: AlertCircleIcon,
                        bgGradient: "from-red-50 to-red-100",
                        textColor: "text-red-900"
                    },
                ]);
            } else {
                toast.error(res.data);
            }
            setLoading(false);
        } catch (error) {
            setLoading(false);
            toast.error("something went wrong");
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        // URL will be updated via useEffect
    };

    const handleViewDetails = (item) => {
        setSelectedRawMaterial(item);
        setShowRawMaterialDetailsModal(true);
    };

    const handleCloseDetails = () => {
        setShowRawMaterialDetailsModal(false);
        setSelectedRawMaterial(null);
    };

    const clearAllFilters = () => {
        setSearchTerm('');
        setFilterCategory('');
        setFilterOrderBy('asc');
        setFilterKit('');
        setCurrentPage(1);

        // Clear URL parameters
        setSearchParams({}, { replace: true });

        // Fetch data with cleared filters
        fetchData({ query: "" });

        toast.success('All filters cleared!');
    };

    return (
        <>
            <Breadcrumbs items={[
                { name: 'Home', link: '/dashboard' },
                { name: 'Inventory' },
                { name: 'Raw Items' },
            ]} />

            <div className="grid grid-cols-4 gap-4 my-2">
                {statsCardsData.map((card, index) => (
                    <Card
                        key={index}
                        title={card.title}
                        count={card.count}
                        icon={card.icon}
                        bgGradient={card.bgGradient}
                        textColor={card.textColor}
                    />
                ))}
            </div>

            <div className="flex justify-between items-center mb-6">
                <StyledHeadingText text={"Raw Items Inventory"} size={"text-3xl"} />
                <Link
                    className="bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-lg"
                    to="/inventory/raw-items/add"
                >
                    Add New Item
                </Link>
            </div>

            {/* Filters Section */}
            <CustomCard title="Filters & Search">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-gray-900">Filter Options</h3>
                    <Button
                        onClick={clearAllFilters}
                        className="bg-gray-500 text-white hover:bg-gray-600 px-3 py-1 text-sm"
                    >
                        Clear All Filters
                    </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="md:col-span-2">
                        <SearchBar
                            value={searchTerm}
                            onSearch={handleSearch}
                            placeholder="Search by name or SKU..."
                        />
                    </div>
                    <DropDown
                        options={categories}
                        value={filterCategory}
                        onChange={handleFilterCategory}
                        placeholder="Filter by Category"
                    />
                    <DropDown
                        options={kitDropdown.map((kit) => ({
                            value: kit.id,
                            label: kit.name
                        }))}
                        value={filterKit}
                        onChange={handleFilterKit}
                        placeholder="Filter by Kit"
                    />
                </div>
            </CustomCard>

            {
                loading ?
                    <div className="flex justify-center items-center h-full">
                        <CustomLoading />
                    </div> :
                    <CustomCard title={`Raw Items`}>
                        <Table
                            headers={[
                                { label: "Item Details", key: "item" },
                                { label: "Stock Status", key: "order_by_quantity", needFilter: true },
                                // { label: "Threshold", key: "threshold" },
                                // { label: "Value", key: "value" },
                                // { label: "Supplier", key: "supplier" },
                                { label: "Location", key: "location" },
                                { label: "Actions", key: "actions" }
                            ]}
                            onFilterChange={(val) => handleOrderBy(val.filter !== "none" ? val.filter : "")}
                            currentSort={'order_by_quantity'}
                            currentSortDir={filterOrderBy}
                            rows={rawItems.results?.map((item) => {
                                // Defensive: handle missing last_raw_material_transaction
                                const lastTxn = item.last_raw_material_transaction || {};
                                // Defensive: handle missing status
                                const status = item.status || "good";
                                // Defensive: handle missing unit
                                const unit = item.unit || lastTxn.unit || "";
                                // Defensive: handle missing quantity
                                const quantity = typeof item.quantity !== "undefined" ? item.quantity : lastTxn.quantity;
                                // Defensive: handle missing threshold
                                const threshold = typeof item.threshold !== "undefined" ? item.threshold : 0;
                                // Defensive: handle missing price
                                const price = lastTxn.price || "";
                                // Defensive: handle missing supplier
                                const supplier = lastTxn.supplier || "";
                                // Defensive: handle missing location
                                const location = lastTxn.location || "";
                                // Defensive: handle missing created_at
                                const lastRestocked = lastTxn.created_at
                                    ? new Date(lastTxn.created_at).toLocaleDateString()
                                    : "N/A";
                                // Value calculation
                                const totalValue = (quantity && price)
                                    ? `₹${(Number(quantity) * Number(price)).toLocaleString()}`
                                    : "N/A";
                                const costPerUnit = price ? `₹${price}` : "N/A";

                                return {
                                    item: (
                                        <div>
                                            <p className="font-semibold">{item.name}</p>
                                            <p className="text-sm text-gray-600">{item.sku} • {item.category}</p>
                                        </div>
                                    ),
                                    order_by_quantity: (
                                        <div className="flex items-center space-x-2">
                                            <div>
                                                <p className="font-semibold">
                                                    {quantity} {unit}
                                                </p>
                                                <span className={getStatusBadge(status)}>
                                                    {status.charAt(0).toUpperCase() + status.slice(1)}
                                                </span>
                                            </div>
                                        </div>
                                    ),
                                    threshold: (
                                        <div className="text-center">
                                            <p className="font-medium">{threshold} {unit}</p>
                                        </div>
                                    ),
                                    value: (
                                        <div>
                                            <p className="font-semibold text-green-600">{totalValue}</p>
                                            <p className="text-sm text-gray-600">{costPerUnit}/unit</p>
                                        </div>
                                    ),
                                    supplier: (
                                        <div>
                                            <p className="font-medium">{supplier}</p>
                                            <p className="text-sm text-gray-600">Last: {lastRestocked}</p>
                                        </div>
                                    ),
                                    location: (
                                        <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                                            {location}
                                        </span>
                                    ),
                                    actions: (
                                        <div className="flex space-x-2">
                                            <Link
                                                className="text-center hover:bg-blue-600 text-white px-2 py-2 text-xs rounded bg-blue-500 self-center"
                                                to={`/inventory/raw-items/edit`}
                                                state={{ item }}
                                            >
                                                Update Stock
                                            </Link>
                                            <Button
                                                onClick={() => handleViewDetails(item)}
                                                className={`text-center hover:bg-green-500 text-white px-2 py-1 text-xs rounded bg-primary`}
                                                children="View Details"
                                            />
                                        </div>
                                    )
                                };
                            })}
                        />
                        <Pagination
                            currentPage={currentPage}
                            totalPages={Math.ceil(rawItems?.count / pageSize)}
                            pageSize={pageSize}
                            totalEntries={rawItems?.count}
                            onPageChange={handlePageChange}
                            data={rawItems}
                            setData={setRawItems}
                        />
                    </CustomCard>
            }
            <ShowModal
                show={showRawMaterialDetailsModal}
                onClose={handleCloseDetails}
                title="Raw Material Details"
                isSidebar={true}
                size="lg"
                componentToPass={<RawInventoryDetailedView rawMaterial={selectedRawMaterial} />}
            />
        </>
    );
};

export default RawItemsInventory; 